import React, { useState, useEffect } from 'react';
import { useTranslation, Locale, LOCALE_FLAGS, LOCALE_LABELS } from '../../i18n';

interface FooterProps {
  locale: Locale;
  setLocale: (lang: Locale) => void;
}

export const Footer: React.FC<FooterProps> = ({ locale, setLocale }) => {
  const { t } = useTranslation();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState<string>('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      const hh = String(now.getHours()).padStart(2, '0');
      const min = String(now.getMinutes()).padStart(2, '0');
      setCurrentTime(`${dd}.${mm}.${yyyy} ${hh}:${min}`);
    };
    updateTime();
    const timer = setInterval(updateTime, 60000);
    return () => clearInterval(timer);
  }, []);

  return (
    <footer className="w-full bg-[#f2f2f2] text-[#454545] border-t border-[#d4d4d4] pt-8 pb-6 mt-10 font-sans">
      <div className="max-w-[1400px] mx-auto px-4 flex flex-col">
        {/* Top Section */}
        <div className="flex flex-col md:flex-row justify-between mb-8 gap-8">
          <div className="flex flex-col gap-2">
            <h4 className="font-bold text-[14px] text-[#212121] mb-2">Informationen</h4>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">AGB</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Datenschutzrichtlinie</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Cookie-Richtlinie</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Spielerschutz</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Tipico Sport Blog</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Angebotsbedingungen</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Cookie-Einstellungen</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Erklärung zur Barrierefreiheit</a>
          </div>
          
          <div className="flex flex-col gap-2">
            <h4 className="font-bold text-[14px] text-[#212121] mb-2">Das Unternehmen</h4>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Wer ist Tipico?</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Karriere</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Impressum</a>
          </div>
          
          <div className="flex flex-col gap-2">
            <h4 className="font-bold text-[14px] text-[#212121] mb-2">Kontakt</h4>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Help Center</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Tipico Gruppe</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Franchiseprogramm</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Affiliateprogramm</a>
            <a href="#" className="text-[13px] hover:underline hover:text-[#212121]">Tipico Streetwear</a>
          </div>

          {/* Language Selector */}
          <div className="flex flex-col items-start min-w-[200px]">
            <div className="relative w-full max-w-[160px]">
              <button 
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="w-full h-[40px] bg-white border border-[#d4d4d4] rounded-[4px] px-3 flex items-center justify-between text-[13px] font-medium text-[#212121] shadow-sm hover:border-[#999999] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[16px]">{LOCALE_FLAGS[locale]}</span>
                  <span>{LOCALE_LABELS[locale]}</span>
                </div>
                <svg className="w-4 h-4 text-[#727272]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {langMenuOpen && (
                <div className="absolute bottom-full right-0 mb-1 w-full bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[100]">
                  {(['en', 'de', 'fr', 'it', 'tr', 'nl'] as Locale[]).map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        setLocale(lang);
                        setLangMenuOpen(false);
                        setTimeout(() => window.location.reload(), 50);
                      }}
                      className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-gray-100 transition ${locale === lang ? 'bg-gray-50 font-bold text-[#D41534]' : 'text-gray-700'}`}
                    >
                      <span className="text-xl">{LOCALE_FLAGS[lang]}</span>
                      <span className="text-[13px]">{LOCALE_LABELS[lang]}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 text-[12px] text-[#727272]">
              {currentTime}
            </div>
          </div>
        </div>

        <div className="border-t border-[#d4d4d4] my-6"></div>

        {/* Middle Section */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-8">
          
          {/* Trust */}
          <div className="flex flex-col gap-4 max-w-[250px]">
            <h4 className="font-bold text-[14px] text-[#212121]">Vertrauen</h4>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="w-[36px] h-[36px] bg-white rounded-full border border-red-500 flex items-center justify-center text-red-500 font-bold text-[15px]">18+</div>
              <div className="h-[24px] bg-white px-2 py-1 flex items-center border border-gray-300 text-[10px] font-bold text-gray-500 rounded">GORDON MOODY</div>
              <div className="h-[24px] bg-white px-2 py-1 flex items-center border border-gray-300 text-[12px] font-bold text-blue-600 rounded">DSW</div>
            </div>
          </div>

          {/* Partners */}
          <div className="flex flex-col gap-4">
            <h4 className="font-bold text-[14px] text-[#212121]">Unsere Partner</h4>
            <div className="flex flex-wrap gap-4 items-center">
              <div className="h-[40px] w-[40px] bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[10px] text-gray-500">BUNDESLIGA</div>
              <div className="h-[40px] w-[40px] bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[10px] text-gray-500">2. LIGA</div>
              <div className="h-[40px] w-[40px] bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[10px] text-gray-500">DFB-POKAL</div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="flex flex-col gap-4 max-w-[400px]">
            <h4 className="font-bold text-[14px] text-[#212121]">Zahlungsmethoden</h4>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-blue-700">Bank Transfer</div>
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-blue-500 italic">PayPal</div>
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-pink-500">Klarna</div>
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-blue-800">paysafecard</div>
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-black"> Pay</div>
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-gray-700">G Pay</div>
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-purple-800">Skrill</div>
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-red-500">Mastercard</div>
              <div className="h-[28px] px-3 bg-white border border-gray-200 rounded flex items-center justify-center font-bold text-[11px] text-blue-900 italic">VISA</div>
            </div>
          </div>
        </div>

        <div className="border-t border-[#d4d4d4] my-6"></div>
        
        {/* Bottom Section */}
        <div className="flex items-center gap-4 text-[10px] text-[#999999]">
           <div className="h-[40px] w-[120px] bg-white border border-gray-300 rounded flex items-center justify-center font-bold text-gray-500 text-center leading-tight">Staatlich erlaubt<br/>und beaufsichtigt</div>
           <div className="w-[36px] h-[36px] bg-white rounded-full border border-red-500 flex items-center justify-center text-red-500 font-bold text-[15px]">18+</div>
           <p className="flex-1">
             Glücksspiel kann süchtig machen. Bitte spiele verantwortungsbewusst. 
           </p>
        </div>

      </div>
    </footer>
  );
};
